import { z } from 'zod'
import { uuidSchema, emailSchema, paginationSchema } from './common'

// User role enum
export const userRoleSchema = z.enum(['HR_ADMIN', 'GENERAL_MANAGER', 'BRANCH_MANAGER', 'SYSTEM_ADMIN'])

// Base user schema (matches database structure)
export const userSchema = z.object({
  id: uuidSchema,
  email: emailSchema,
  firstName: z.string().min(1, 'First name is required').max(100, 'First name too long'),
  lastName: z.string().min(1, 'Last name is required').max(100, 'Last name too long'),
  role: userRoleSchema,
  createdAt: z.date(),
  updatedAt: z.date()
})

// Schema for user creation
export const createUserSchema = z.object({
  email: emailSchema,
  password: z.string().min(8, 'Password must be at least 8 characters'),
  firstName: z.string().min(1, 'First name is required').max(100, 'First name too long'),
  lastName: z.string().min(1, 'Last name is required').max(100, 'Last name too long'),
  role: userRoleSchema,
  branchId: z.string().max(50, 'Branch ID too long').optional()
})

// Schema for user updates (all fields optional for PATCH operations)
export const updateUserSchema = z.object({
  email: emailSchema.optional(),
  password: z.string()
    .transform(val => val === '' ? undefined : val)
    .optional()
    .refine(val => val === undefined || val.length >= 8, {
      message: 'Password must be at least 8 characters'
    }),
  firstName: z.string().min(1, 'First name is required').max(100, 'First name too long').optional(),
  lastName: z.string().min(1, 'Last name is required').max(100, 'Last name too long').optional(),
  role: userRoleSchema.optional(),
  branchId: z.string().max(50, 'Branch ID too long').optional().nullable()
})

// Schema for user queries with pagination and filtering (for URL query parameters)
export const getUsersQuerySchema = z.object({
  page: z.string().regex(/^\d+$/).optional().default('1').transform(Number),
  limit: z.string().regex(/^\d+$/).optional().default('10').transform(Number),
  role: userRoleSchema.optional(),
  search: z.string().optional()
})

// User selection object (excludes password)
export const userSelectFields = {
  id: true,
  email: true,
  firstName: true,
  lastName: true,
  role: true,
  createdAt: true,
  updatedAt: true
} as const

// Type exports
export type UserData = z.infer<typeof userSchema>
export type CreateUserData = z.infer<typeof createUserSchema>
export type UpdateUserData = z.infer<typeof updateUserSchema>
export type GetUsersQuery = z.infer<typeof getUsersQuerySchema>
export type UserRole = z.infer<typeof userRoleSchema> 